import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface NavigationProps {
  scrolled: boolean;
}

export default function Navigation({ scrolled }: NavigationProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const scrollToSection = (id: string) => {
    setMobileOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavClick = (path: string) => {
    setMobileOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
    navigate(path);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileOpen]);

  const navLinks = [
    { label: 'About', path: '/about' },
    { label: 'Service Areas', path: '/service-areas' },
    { label: 'FAQs', path: '/faqs' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4" ref={menuRef}>
        <div className="flex items-center justify-between">
          <div className="flex-shrink-0 mr-8">
            <img
              src={
                scrolled || mobileOpen
                  ? 'https://qymceftywmiiwdrmhnwb.supabase.co/storage/v1/object/public/files/evernorth-ai/default%20logo%20blue.svg'
                  : 'https://qymceftywmiiwdrmhnwb.supabase.co/storage/v1/object/public/files/evernorth-ai/default%20logo%20white.svg'
              }
              alt="Evernorth AI Logo"
              className="h-10 lg:h-16 w-auto cursor-pointer"
              onClick={() => scrollToSection('hero')}
            />
          </div>

          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            <button
              onClick={() => scrollToSection('hero')}
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-charcoal hover:text-navy' : 'text-white/80 hover:text-white'
              }`}
            >
              Home
            </button>

            <div className="relative" ref={servicesRef}>
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                  scrolled ? 'text-charcoal hover:text-navy' : 'text-white/80 hover:text-white'
                }`}
              >
                Services
                <i
                  className={`ri-arrow-down-s-line text-xs transition-transform ${
                    servicesOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 animate-fade-in">
                  <button
                    onClick={() => handleNavClick('/services')}
                    className="w-full text-left px-4 py-2.5 text-sm text-charcoal hover:bg-gray-50 hover:text-navy transition-colors cursor-pointer"
                  >
                    All Services
                  </button>
                  <button
                    onClick={() => handleNavClick('/workshops/half-day')}
                    className="w-full text-left px-4 py-2.5 text-sm text-charcoal hover:bg-gray-50 hover:text-navy transition-colors cursor-pointer"
                  >
                    Half Day Workshop
                  </button>
                  <button
                    onClick={() => handleNavClick('/workshops/full-day')}
                    className="w-full text-left px-4 py-2.5 text-sm text-charcoal hover:bg-gray-50 hover:text-navy transition-colors cursor-pointer"
                  >
                    Full Day Workshop
                  </button>
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled ? 'text-charcoal hover:text-navy' : 'text-white/80 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}

            <Link
              to="/contact"
              className="px-6 py-2.5 bg-gold text-white text-sm font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap"
            >
              Get Started
            </Link>
          </div>

          <button
            className={`lg:hidden cursor-pointer ${
              scrolled || mobileOpen ? 'text-navy' : 'text-white'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden mt-4 pb-4 animate-fade-in">
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => scrollToSection('hero')}
                className="text-sm font-medium transition-colors text-left cursor-pointer text-charcoal hover:text-navy"
              >
                Home
              </button>

              <div>
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="text-sm font-medium transition-colors text-left cursor-pointer text-charcoal hover:text-navy flex items-center gap-1 w-full"
                >
                  Services
                  <i
                    className={`ri-arrow-down-s-line text-xs transition-transform ${
                      mobileServicesOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {mobileServicesOpen && (
                  <div className="ml-4 mt-2 flex flex-col space-y-2">
                    <button
                      onClick={() => handleNavClick('/services')}
                      className="text-sm text-charcoal/70 hover:text-navy transition-colors text-left cursor-pointer"
                    >
                      All Services
                    </button>
                    <button
                      onClick={() => handleNavClick('/workshops/half-day')}
                      className="text-sm text-charcoal/70 hover:text-navy transition-colors text-left cursor-pointer"
                    >
                      Half Day Workshop
                    </button>
                    <button
                      onClick={() => handleNavClick('/workshops/full-day')}
                      className="text-sm text-charcoal/70 hover:text-navy transition-colors text-left cursor-pointer"
                    >
                      Full Day Workshop
                    </button>
                  </div>
                )}
              </div>

              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className="text-sm font-medium transition-colors text-left cursor-pointer text-charcoal hover:text-navy"
                >
                  {link.label}
                </Link>
              ))}

              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                className="px-6 py-2.5 bg-gold text-white text-sm font-semibold rounded-full hover:bg-[#a68a4e] transition-all cursor-pointer whitespace-nowrap text-center"
              >
                Get Started
              </Link>

              <div className="flex gap-3 justify-center pt-3 border-t border-charcoal/10">
                <a
                  href="https://www.facebook.com/people/Evernorth-AI/61584730084444/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center bg-navy/10 rounded-lg hover:bg-gold hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-facebook-fill text-navy text-base"></i>
                </a>
                <a
                  href="https://x.com/EvernorthAI"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center bg-navy/10 rounded-lg hover:bg-gold hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-twitter-x-line text-navy text-base"></i>
                </a>
                <a
                  href="https://www.linkedin.com/company/evernorthai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center bg-navy/10 rounded-lg hover:bg-gold hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-linkedin-fill text-navy text-base"></i>
                </a>
                <a
                  href="https://www.youtube.com/@Evernorth_AI"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center bg-navy/10 rounded-lg hover:bg-gold hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-youtube-fill text-navy text-base"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
